import { CalendarCheck, CheckCircle2, FileSignature, Inbox, MessageCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { intlLocale } from '@/lib/i18n';

/* ---------------------------------------------------------------------------
   ההמחשה ב-hero של דף המכירה: לוח היום, הצעת מחיר חתומה והודעת וואטסאפ —
   ב-HTML/CSS, לא צילום מסך. הנתונים דוגמה (מסומן "המחשה"), לא לקוחות אמיתיים.
   לקוראי מסך — תיאור אחד (role="img" + aria-label), בלי לקרוא כל פרט.
   --------------------------------------------------------------------------- */

interface Stat {
  label: string;
  value: string;
}
interface Row {
  title: string;
  meta: string;
  status: string;
}

const ROW_TONES = [
  'bg-success-subtle text-success ring-success-border/60',
  'bg-warning-subtle text-warning ring-warning-border/60',
  'bg-violet-subtle text-violet ring-violet-border/60',
];
const STAT_TONES = ['text-accent', 'text-teal', 'text-violet'];

export function HeroVisual() {
  const { t } = useTranslation('public');
  const stats = t('landing.mock.stats', { returnObjects: true }) as Stat[];
  const rows = t('landing.mock.rows', { returnObjects: true }) as Row[];
  const amount = new Intl.NumberFormat(intlLocale(), { style: 'currency', currency: 'ILS', maximumFractionDigits: 0 }).format(4200);

  return (
    <div role="img" aria-label={t('landing.mock.aria')} className="relative mx-auto w-full max-w-md lg:max-w-none">
      {/* חלון האפליקציה */}
      <div className="overflow-hidden rounded-(--radius-xl) bg-surface-raised text-fg shadow-lg ring-1 ring-white/10">
        <div className="flex items-center gap-1.5 border-b border-border bg-surface-sunken px-4 py-2.5">
          <span className="size-2.5 rounded-full bg-danger/70" />
          <span className="size-2.5 rounded-full bg-warning/70" />
          <span className="size-2.5 rounded-full bg-success/70" />
          <span className="ms-3 rounded-full bg-accent-subtle px-2 py-0.5 text-2xs font-medium text-accent">
            {t('landing.mock.caption')}
          </span>
        </div>
        <div className="space-y-4 p-4 sm:p-5">
          <p className="text-sm font-semibold">{t('landing.mock.today')}</p>
          <div className="grid grid-cols-3 gap-2">
            {stats.map((s, i) => (
              <div key={s.label} className="rounded-(--radius-md) border border-border bg-surface p-2.5">
                <p className={`tabular text-xl font-bold ${STAT_TONES[i % STAT_TONES.length]}`}>{s.value}</p>
                <p className="text-2xs leading-tight text-fg-muted">{s.label}</p>
              </div>
            ))}
          </div>
          <div className="space-y-2">
            <p className="flex items-center gap-1.5 text-xs font-medium text-fg-muted">
              <Inbox className="size-3.5" aria-hidden />
              {t('landing.mock.listTitle')}
            </p>
            <ul className="space-y-1.5">
              {rows.map((r, i) => (
                <li key={r.title} className="flex items-center gap-3 rounded-(--radius-md) border border-border bg-surface px-3 py-2">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-xs font-medium">{r.title}</span>
                    <span className="block truncate text-2xs text-fg-subtle">{r.meta}</span>
                  </span>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-2xs font-medium ring-1 ring-inset ${ROW_TONES[i % ROW_TONES.length]}`}>
                    {r.status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          {/* מקום להודעת הוואטסאפ שצפה מעל, במסכים צרים היא בזרימה */}
          <div className="hidden h-28 sm:block" />
        </div>
      </div>

      {/* הצעת מחיר חתומה — צפה מעל הפינה העליונה */}
      <div className="mt-3 w-full rounded-(--radius-lg) bg-surface-raised p-3 text-fg shadow-lg ring-1 ring-border sm:absolute sm:-top-6 sm:end-[-1.5rem] sm:mt-0 sm:w-60">
        <div className="flex items-center gap-2">
          <span className="grid size-8 place-items-center rounded-(--radius-md) bg-violet text-fg-on-accent">
            <FileSignature className="size-4" aria-hidden />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-semibold">{t('landing.mock.quoteTitle')}</span>
            <span className="flex items-center gap-1 text-2xs font-medium text-success">
              <CheckCircle2 className="size-3" aria-hidden />
              {t('landing.mock.quoteSigned')}
            </span>
          </span>
          <span className="tabular text-sm font-bold">{amount}</span>
        </div>
        <p className="mt-2 border-t border-border pt-2 text-2xs text-fg-muted">{t('landing.mock.quoteNext')}</p>
      </div>

      {/* הודעת וואטסאפ ללקוח — צפה מעל הפינה התחתונה */}
      <div className="mt-3 w-full rounded-(--radius-lg) bg-surface-raised p-3 text-fg shadow-lg ring-1 ring-border sm:absolute sm:-bottom-8 sm:start-[-1.5rem] sm:mt-0 sm:w-72">
        <p className="flex items-center gap-1.5 text-2xs font-semibold text-success">
          <MessageCircle className="size-3.5" aria-hidden />
          {t('landing.mock.waFrom')}
        </p>
        <div className="mt-1.5 rounded-(--radius-md) bg-success-subtle p-2.5 text-xs leading-relaxed">
          {t('landing.mock.waText')}{' '}
          <span className="font-semibold text-accent underline underline-offset-2">{t('landing.mock.waLink')}</span>
        </div>
        <p className="mt-1.5 flex items-center gap-1 text-2xs font-medium text-fg-muted">
          <CalendarCheck className="size-3 text-success" aria-hidden />
          {t('landing.mock.waConfirmed')}
        </p>
      </div>
    </div>
  );
}
